/** @jsxImportSource @emotion/react */

import classNames from 'classnames';
import { ChevronLeft, ChevronRight, CloudCog, Joystick as JoystickIcon, RefreshCw } from 'lucide-react';
import { ConnectionType } from 'modules/core/supabase/tables/match-chronology';
import { resetGameInfo, syncGameInfo } from 'modules/overlay-v2/store/game-info/actions';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useIsMobileImproved from 'shared/hooks/use-is-mobile-improved';
import EmptyButton from 'ud-ui/components/empty-button';
import UDText from 'ud-ui/components/text';
import supabase from '../../../../core/supabase/supabase';
import { time } from 'modules/overlay-v2/domain/constants/time';
import { setHideControlPanel } from '../../../store/overlay/overlay';
import { selectIsHideControlPanel } from '../../../store/overlay/selectors';
import { resetTime, syncTime } from 'modules/overlay-v2/store/time/actions';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import Indicator from 'modules/overlay-v2/ui/components/components/indicator';
import Text from 'modules/overlay-v2/ui/components/components/text';
import { Dashboard } from './dashboard';
import { Fouls } from 'modules/overlay-v2/ui/components/joystick/fouls';
import { Period } from 'modules/overlay-v2/ui/components/joystick/period';
import { Score } from 'modules/overlay-v2/ui/components/joystick/score';
import { Button, HideButton, MainBlock, OpenPanelWrapper } from 'modules/overlay-v2/ui/components/joystick/style';
import Team from 'modules/overlay-v2/ui/components/joystick/team';
import { TeamsName } from 'modules/overlay-v2/ui/components/joystick/teams-name';

function Joystick() {
  const { matchId } = useParams() as { matchId: string };
  const isMobileImproved = useIsMobileImproved();

  const dispatch = useDispatch();
  const { match, isSynchronization } = useGameInfo();
  const [isJoystickActive, setIsJoystickActive] = useState(true);
  const [ethernetConnection, setEthernetConnection] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(supabase.matchChronology.connectionStatus.value);
  const [prevConnectionStatus, setPrevConnectionStatus] = useState<ConnectionType>();
  const isHideControlPanel = useSelector(selectIsHideControlPanel);

  const [devDisableConnection, setDevDisableConnection] = useState(false);

  const isDev = process.env.REACT_APP_ENVIRONMENT !== 'production';
  const timer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isMobileImproved) {
      dispatch(setHideControlPanel(true));
    }
  }, [dispatch, isMobileImproved]);

  useEffect(() => {
    supabase.matchChronology.connectionStatus.subscribe((data, prevValue) => {
      setConnectionStatus(data);
      setPrevConnectionStatus(prevValue);
    });

    const activeEthernet = () => {
      setEthernetConnection(true);
      console.debug({ event: 'internet connection recovered' });
    };
    const disableEthernet = () => {
      setEthernetConnection(false);
      console.error({ event: 'internet connection lost' });
    };

    window.addEventListener('online', activeEthernet);
    window.addEventListener('offline', disableEthernet);
    return () => {
      window.removeEventListener('online', activeEthernet);
      window.removeEventListener('offline', disableEthernet);
    };
  }, []);

  useEffect(() => {
    if (connectionStatus === 'SUBSCRIBED' && ethernetConnection) {
      setIsJoystickActive(true);
    } else {
      setIsJoystickActive(false);
    }
  }, [connectionStatus, ethernetConnection]);

  const onClickReset = useCallback(() => {
    dispatch(resetGameInfo());
    dispatch(resetTime());
  }, [dispatch]);

  const handleClickSync = useCallback(() => {
    if (isSynchronization) return;
    if (!match) return;
    const matchId = match.id;
    dispatch(syncGameInfo({ matchId }));
    dispatch(syncTime({ matchId }));
  }, [dispatch, isSynchronization, match]);

  const handleUpdateData = useCallback(() => {
    supabase.matchChronology.getRow(Number(matchId)).then((newData) => {
      if (!newData) return;
      const currentDate = new Date().toDateString();
      supabase.matchChronology.handleRecordUpdated({ new: newData, commit_timestamp: currentDate });
    });
  }, [matchId]);

  useEffect(() => {
    if (connectionStatus !== 'SUBSCRIBED' && prevConnectionStatus === 'SUBSCRIBED') {
      supabase.overlayLogs.createInfo({
        message: 'Нет соединения с джойстиком. Включен режим запроса данных.',
      });
      timer.current = setInterval(handleUpdateData, time.second * 3);
    } else {
      if (connectionStatus === 'SUBSCRIBED' && prevConnectionStatus && prevConnectionStatus !== 'SUBSCRIBED') {
        supabase.overlayLogs.createInfo({
          message: 'Соединение с джойстиком установленно. Режим запроса данных выключен.',
        });
        clearInterval(timer.current);
      }
    }
  }, [connectionStatus, handleUpdateData, prevConnectionStatus]);

  const toggleHideControlPanel = useCallback(() => {
    dispatch(setHideControlPanel(!isHideControlPanel));
  }, [dispatch, isHideControlPanel]);

  const handleDisconnect = useCallback(() => {
    setDevDisableConnection((prevState) => {
      if (prevState) {
        supabase.matchChronology.observeChanges(Number(matchId));
      } else {
        supabase.matchChronology.stopObserveChanges().then((result) => {
          console.debug({ disableConnection: result });
          if (result === 'ok') {
            setConnectionStatus('CLOSED');
          }
        });
      }

      return !prevState;
    });
  }, [matchId]);

  return isHideControlPanel ? (
    <>
      <div style={{ position: 'relative' }} />
      <OpenPanelWrapper opacity={0} position={'right'}>
        <HideButton color={'white'} onClick={toggleHideControlPanel} icon={<ChevronLeft style={{ zIndex: 1000 }} />} />
      </OpenPanelWrapper>
    </>
  ) : (
    <>
      <MainBlock className={'flex flex-col'} style={{ zIndex: 99 }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <EmptyButton
            style={{ backgroundColor: '#D9B244', height: '100%', width: 34, minWidth: 34 }}
            color={'white'}
            onClick={toggleHideControlPanel}
            icon={<ChevronRight style={{ zIndex: 1000 }} />}
          />
          <Indicator
            className={'w-full'}
            text={isJoystickActive ? 'Соединение с джойстиком установленно!' : 'Нет соединения с джойстиком!'}
            icon={<JoystickIcon size={20} />}
            isActive={isJoystickActive}
          />
        </div>
        {isDev && (
          <Button color={devDisableConnection ? 'darkgreen' : 'darkred'} onClick={handleDisconnect} className={'flex-center'}>
            <Text size={'medium'} color={'inherit'}>
              {devDisableConnection ? 'Включить соединение' : 'Разорвать соединение'}
            </Text>
            <UDText type={'body-text'} style={{ color: 'inherit' }}>
              {devDisableConnection ? 'V' : 'X'}
            </UDText>
          </Button>
        )}
        <TeamsName />
        <Score />
        <Fouls />
        <Period />
        <Dashboard />
        <Button color={'#D9B244'} onClick={handleClickSync} className={classNames('flex-center', { loading: isSynchronization })}>
          {!isSynchronization && (
            <>
              <Text size={'medium'} color={'inherit'}>
                Синхронизировать
              </Text>
              <CloudCog size={14} />
            </>
          )}
        </Button>
        <Button color={'#686A79'} onClick={onClickReset} className={'flex-center'}>
          <Text size={'medium'} color={'inherit'}>
            Сбросить все настройки
          </Text>
          <RefreshCw size={14} />
        </Button>
        <Team teamIndex={0} />
        <Team teamIndex={1} />
      </MainBlock>
    </>
  );
}

export default Joystick;
